import React, { useEffect, useState } from 'react';

import { useConciergeContext } from '../../ConciergeContextProvider';
import type { ConciergeCommerceProduct } from '../../types/commerce';
import type { ConciergeProductV2Detail } from '../../types';
import { INK } from './constants';
import { useBagWords } from '../../../utils/hooks/useBagWords';

/**
 * "Add to bag" for the product detail panel. It is the ONE buy control in the
 * concierge — the product cards carry none.
 *
 * ## Which bag, and when it appears
 *
 * It renders nothing unless a {@link ConciergeCommerceAdapter} was injected into
 * the concierge context, and the host injects one only when this app HAS a bag:
 * the Brandclub bag when the CHECKOUT capability is on (D-49 — true on the
 * Shopify App Proxy too, where checkout is served at `/a/sos/checkout`), else
 * the Shopify storefront bag on a Shopify surface, else nothing. So a brand with
 * neither gets `commerce === undefined`, this component is a no-op, and
 * `PriceBlock` keeps its plain "View" link instead. There is no surface flag to
 * keep in sync.
 *
 * The adapter's own `canAddToCart` then re-checks per product, so a product we
 * could not actually add never shows a button that would fail.
 *
 * That check cannot be complete, though: whether a sku has a fulfillment ROUTE
 * is decided server-side, at add time, by the MCF eligibility gate — there is no
 * field on the product that mirrors it. So an add can still be refused, and when
 * it is, this button retires itself and tells its parent, which puts the plain
 * "View" link back in the slot. An item we cannot sell should still be reachable
 * at the retailer, not a dead space where a button used to be.
 *
 * Deliberately mirrors `ProductCardComponent`'s proven behaviour rather than
 * inventing its own: transient status, auto-reset after 2s, and — importantly —
 * the button REMOVES itself when an add fails, because the failure that matters
 * here (`product_lookup_404`) means this product does not exist in the
 * merchant's storefront, so retrying can only fail again.
 */
export function AddToBagButton({
  detail,
  onUnavailable,
}: {
  detail: ConciergeProductV2Detail;
  /** Called when this product turns out not to be buyable, so the parent can fall back to "View". */
  onUnavailable?: () => void;
}) {
  const { commerce } = useConciergeContext();
  const { bag } = useBagWords();
  const [status, setStatus] = useState<'idle' | 'adding' | 'added' | 'failed'>('idle');

  // A different product in the same panel starts clean — otherwise a previous
  // product's "Added ✓" (or its failure) leaks onto the next one.
  useEffect(() => {
    setStatus('idle');
  }, [detail.stacklineSku]);

  useEffect(() => {
    if (status !== 'added') return undefined;
    const timer = setTimeout(() => setStatus('idle'), 2000);
    return () => clearTimeout(timer);
  }, [status]);

  // Pass the DISPLAY detail's url/retailer: when a variant is selected the panel
  // rewrites both from that variant's offer, and the `?variant=` hint in the url
  // is what makes the adapter add the right variant instead of the default one.
  const cartProduct: ConciergeCommerceProduct = {
    stacklineSku: detail.stacklineSku,
    title: detail.title,
    retailerId: detail.retailerId,
    retailerProductPageUrl: detail.retailerProductPageUrl,
    quantity: 1,
    // The Brandclub bag writes a real cart line keyed by the RETAILER's sku, so
    // it needs these; the Shopify adapter ignores them and resolves the variant
    // from the url instead.
    retailerSku: detail.retailerSku,
    brandId: detail.brandId,
    retailPrice: detail.retailPrice,
    isAvailable: detail.isAvailable,
  };

  if (!commerce || !commerce.canAddToCart(cartProduct) || status === 'failed') return null;

  const handleClick = async () => {
    if (status === 'adding') return;
    setStatus('adding');
    try {
      const result = await commerce.addToCart(cartProduct);
      setStatus(result.ok ? 'added' : 'failed');
      if (!result.ok) onUnavailable?.();
    } catch {
      setStatus('failed');
      onUnavailable?.();
    }
  };

  const label = status === 'adding' ? 'Adding…' : status === 'added' ? 'Added ✓' : `Add to ${bag}`;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={status === 'adding'}
      aria-label={`Add ${detail.title ?? 'product'} to ${bag}`}
      // Opts out of the concierge stylesheet's grey `button[aria-label]`
      // override, which is meant for header icon buttons and otherwise forces
      // grey-600 text onto this filled dark button — making it look disabled.
      data-concierge-filled-action=""
      style={{
        // Filled with the brand foreground — the panel root publishes
        // `--bc-foreground`, so this matches the card's own filled controls.
        // Geometry is deliberately the "View" link's, because it takes that
        // link's place in the brand row (see `PriceBlock`).
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: INK,
        color: '#FFFFFF',
        border: 'none',
        borderRadius: 100,
        padding: '8px 16px',
        fontFamily: 'inherit',
        fontSize: 12,
        fontWeight: 500,
        lineHeight: '20px',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        cursor: status === 'adding' ? 'default' : 'pointer',
        opacity: status === 'adding' ? 0.7 : 1,
      }}
    >
      {label}
    </button>
  );
}
